/* eslint-disable jsx-a11y/label-has-associated-control, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-element-interactions */
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaPalette, 
  FaImages, 
  FaQuestionCircle, 
  FaGlobe, 
  FaCheckCircle, 
  FaTimesCircle, 
  FaTrash, 
  FaPlus, 
  FaEdit,
  FaCloudUploadAlt,
  FaTimes
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import axiosInstance from '../apis/axiosConfig';
import ConfirmModal from '../common/ConfirmModal';
import './EditFaq.css';

const EditFaq = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0); // 0 to 3
  const [loading, setLoading] = useState(true);

  // Tab configurations
  const [sections, setSections] = useState([
    { id: 1, section: 'Basic Info', icon: FaPalette, status: 1, subtitle: 'Default', validation: 'validation-success' },
    { id: 2, section: 'Banner Configuration', icon: FaImages, status: 0, subtitle: 'Default', validation: 'validation-error' },
    { id: 3, section: 'FAQs', icon: FaQuestionCircle, status: 1, subtitle: 'Default', validation: 'validation-success' },
    { id: 4, section: 'SEO Manager', icon: FaGlobe, status: 1, subtitle: 'Default', validation: 'validation-success' }
  ]);

  // Tab 0: Basic Info
  const [basicInfo, setBasicInfo] = useState({
    page_title: 'Frequently Asked Questions',
    short_description: "Have questions about Aristo India's modular wardrobes, installation, or delivery in Bangalore? Find quick answers right here."
  });

  // Tab 1: Banner Configuration
  const [bannerSlides, setBannerSlides] = useState([
    { id: '1', heading: '', description: '', image: '', alt: '', status: 1 }
  ]);

  // Tab 2: FAQs List Table
  const [faqItems, setFaqItems] = useState([]);

  // Modals for CRUD
  const [showFaqModal, setShowFaqModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [currentFaqItem, setCurrentFaqItem] = useState({ id: null, faq: '', description: '', status: 1 });
  const [deleteFaqTarget, setDeleteFaqTarget] = useState(null);
  const [isDeletingFaq, setIsDeletingFaq] = useState(false);
  const [editFaqTarget, setEditFaqTarget] = useState(null);

  // Tab 3: SEO
  const [seo, setSeo] = useState({
    meta_title: 'FAQs – Aristo India | Modular Wardrobes in Bangalore',
    meta_description: "Find answers to common questions about Aristo India's modular wardrobes, installation, delivery, and services in Bangalore. Get informed before you buy!",
    meta_keywords: []
  });
  const [keywordInput, setKeywordInput] = useState('');

  const fileInputRefs = useRef({});

  useEffect(() => {
    fetchFaqData();
  }, []);

  const fetchFaqData = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/pages/faq');
      if (res && res.data && res.data.success) {
        const { page, faqs } = res.data;
        if (page) {
          setBasicInfo({
            page_title: page.page_heading || '',
            short_description: page.short_description || ''
          });

          // Parse Banner configuration
          if (page.banner) {
            try {
              const parsedBanner = typeof page.banner === 'string' ? JSON.parse(page.banner) : page.banner;
              if (Array.isArray(parsedBanner) && parsedBanner.length > 0) {
                setBannerSlides(parsedBanner);
              }
            } catch (e) {
              console.error("Banner parse error:", e);
            }
          }

          // Parse SEO tags
          const keywordsArray = page.meta_keywords 
            ? page.meta_keywords.split(',').map(k => k.trim()).filter(Boolean)
            : [];
          setSeo({
            meta_title: page.meta_title || '',
            meta_description: page.meta_description || '',
            meta_keywords: keywordsArray
          });

          // Update sidebar toggle states
          const updatedSec = [...sections];
          updatedSec[1].status = page.banner_status || 0; // Banner Config status
          updatedSec[1].validation = page.banner_status ? 'validation-success' : 'validation-error';
          setSections(updatedSec);
        }

        if (faqs) {
          setFaqItems(faqs);
        }
      }
    } catch (err) {
      console.error("Error loading FAQs page data:", err);
      toast.error("Failed to load FAQs configuration");
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (image) => {
    if (!image) return null;
    if (image.startsWith('blob:') || image.startsWith('http')) return image;
    const backendHost = (process.env.REACT_APP_BACKEND || (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL.split('/api')[0]) || 'http://localhost:8000').replace(/\/$/, '');
    return `${backendHost}/uploads/${image}`;
  };

  // Toggle switch sidebar section status
  const handleToggleSectionStatus = (idx, e) => {
    e.stopPropagation();
    const updated = [...sections];
    updated[idx].status = updated[idx].status === 1 ? 0 : 1;
    updated[idx].validation = updated[idx].status === 1 ? 'validation-success' : 'validation-error';
    setSections(updated);
  };

  // Slides CRUD
  const handleAddSlide = () => {
    const newSlide = {
      id: String(bannerSlides.length + 1),
      heading: '',
      description: '',
      image: '',
      alt: '',
      status: 1
    };
    setBannerSlides([...bannerSlides, newSlide]);
    toast.success('Slide added!');
  };

  const handleRemoveSlide = (idx) => {
    if (bannerSlides.length <= 1) {
      toast.warning('At least one slide is required!');
      return;
    }
    setBannerSlides(bannerSlides.filter((_, i) => i !== idx));
    toast.info('Slide removed');
  };

  const handleSlideImageChange = (e, idx) => {
    const file = e.target.files[0];
    if (file) {
      const tempUrl = URL.createObjectURL(file);
      const updated = [...bannerSlides];
      updated[idx].image = tempUrl;
      updated[idx]._file = file;
      setBannerSlides(updated);
      toast.success('Slide image selected!');
    }
  };

  // Keyword tag manager
  const handleAddKeyword = (e) => {
    if (e.key === ',' || e.key === 'Enter') {
      e.preventDefault();
      const val = keywordInput.trim().replace(/,/g, '');
      if (val && !seo.meta_keywords.includes(val)) {
        setSeo({
          ...seo,
          meta_keywords: [...seo.meta_keywords, val]
        });
        setKeywordInput('');
      }
    }
  };

  const handleRemoveKeyword = (kw) => {
    setSeo({
      ...seo,
      meta_keywords: seo.meta_keywords.filter(k => k !== kw)
    });
  };

  // FAQ Item CRUD APIs
  const handleToggleFaqItemStatus = async (item) => {
    const nextStatus = item.status === 1 ? 0 : 1;
    try {
      const res = await axiosInstance.put(`/pages/faq/item/${item.id}`, {
        faq: item.faq,
        description: item.description,
        status: nextStatus
      });
      if (res.data && res.data.success) {
        setFaqItems(faqItems.map(f => f.id === item.id ? { ...f, status: nextStatus } : f));
        toast.success(`FAQ item status updated!`);
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to toggle status');
    }
  };

  const handleOpenFaqModal = (mode, item = null) => {
    setModalMode(mode);
    if (mode === 'edit' && item) {
      setCurrentFaqItem({ id: item.id, faq: item.faq, description: item.description, status: item.status });
    } else {
      setCurrentFaqItem({ id: null, faq: '', description: '', status: 1 });
    }
    setShowFaqModal(true);
  };

  const handleConfirmEditFaq = () => {
    if (editFaqTarget) {
      const item = editFaqTarget;
      setEditFaqTarget(null);
      handleOpenFaqModal('edit', item);
    }
  };

  const handleSaveFaqItem = async () => {
    if (!currentFaqItem.faq.trim() || !currentFaqItem.description.trim()) {
      toast.warning('Please enter both Question and Answer!');
      return;
    }
    try {
      if (modalMode === 'create') {
        const res = await axiosInstance.post('/pages/faq/item', currentFaqItem);
        if (res.data && res.data.success) {
          toast.success('FAQ item created!');
          fetchFaqData(); // reload faq list
          setShowFaqModal(false);
        }
      } else {
        const res = await axiosInstance.put(`/pages/faq/item/${currentFaqItem.id}`, currentFaqItem);
        if (res.data && res.data.success) {
          toast.success('FAQ item updated!');
          fetchFaqData(); // reload faq list
          setShowFaqModal(false);
        }
      }
    } catch (e) {
      console.error(e);
      toast.error('Error saving FAQ item');
    }
  };

  const handleDeleteFaqItem = (item) => {
    setDeleteFaqTarget(item);
  };

  const handleConfirmDeleteFaq = async () => {
    if (!deleteFaqTarget) return;
    try {
      setIsDeletingFaq(true);
      const res = await axiosInstance.delete(`/pages/faq/item/${deleteFaqTarget.id}`);
      if (res.data && res.data.success) {
        setFaqItems(faqItems.filter(f => f.id !== deleteFaqTarget.id));
        toast.success('FAQ item deleted!');
        setDeleteFaqTarget(null);
      }
    } catch (e) {
      console.error(e);
      toast.error('Error deleting FAQ item');
    } finally {
      setIsDeletingFaq(false);
    }
  };

  // Save FAQ page settings (Basic info, Banner, SEO)
  const handleSaveSettings = async () => {
    try {
      const payload = {
        page_heading: basicInfo.page_title,
        short_description: basicInfo.short_description,
        banner: bannerSlides,
        banner_status: sections[1].status,
        meta_title: seo.meta_title,
        meta_description: seo.meta_description,
        meta_keywords: seo.meta_keywords.join(',')
      };

      const res = await axiosInstance.patch('/pages/faq', payload);
      if (res.data && res.data.success) {
        toast.success('FAQ page settings saved successfully!');
      }
    } catch (e) {
      console.error("Save error:", e);
      toast.error('Failed to save FAQ page settings');
    }
  };

  if (loading) {
    return (
      <div className="edit-faq-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <h3 style={{ color: '#c4a882' }}>Loading FAQs Configuration...</h3>
      </div>
    );
  }

  return (
    <div className="edit-faq-container animate-fade-up">
      <div className="edit-faq-header">
        <h1 className="edit-faq-title">FAQs</h1>
      </div>

      <div className="edit-faq-grid">
        {/* Sticky Left Sidebar */}
        <div className="edit-faq-sidebar">
          <ul className="edit-faq-nav-list">
            {sections.map((sec, idx) => {
              const IconComp = sec.icon;
              return (
                <li 
                  key={sec.id || idx}
                  className={`edit-faq-nav-item ${activeTab === idx ? 'active' : ''}`}
                  onClick={() => setActiveTab(idx)}
                >
                  <div className="edit-faq-nav-left">
                    <span className="edit-faq-nav-icon"><IconComp /></span>
                    <div className="edit-faq-nav-text">
                      <span className="nav-item-title">{sec.section}</span>
                      <span className="nav-item-subtitle">{sec.subtitle}</span>
                    </div>
                  </div>

                  <div className="edit-faq-nav-right">
                    <span className={`status-indicator ${sec.validation}`}>
                      {sec.validation === 'validation-success' && <FaCheckCircle />}
                      {sec.validation === 'validation-error' && <FaTimesCircle />}
                    </span>

                    <label className="switch-toggle" onClick={(e) => e.stopPropagation()}>
                      <input 
                        type="checkbox" 
                        checked={sec.status === 1}
                        onChange={(e) => handleToggleSectionStatus(idx, e)}
                      />
                      <span className="switch-slider"></span>
                    </label>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Right Scrollable Panel */}
        <div className="edit-faq-content-panel">
          {activeTab < 3 && sections[activeTab].status === 0 ? (
            <div className="oops-disabled-card animate-fade-up">
              <h2 className="oops-title">{sections[activeTab].section}</h2>
              <div className="oops-status-badge inactive">
                <FaTimesCircle /> Inactive
              </div>
              <p className="oops-message">
                This section is currently inactive. Enable it below to modify the content.
              </p>
              <button 
                type="button" 
                className="btn-enable-activate"
                onClick={() => {
                  const updated = [...sections];
                  updated[activeTab].status = 1;
                  updated[activeTab].validation = 'success';
                  setSections(updated);
                  toast.success(`${sections[activeTab].section} enabled!`);
                }}
              >
                Enable / Activate
              </button>
            </div>
          ) : (
            <>
              {/* TAB 0: Basic Info */}
              {activeTab === 0 && (
                <div className="tab-pane animate-fade-up">
                  <h2 className="edit-faq-panel-title">Basic Info</h2>
                  <div className="faq-card-block">
                    <div className="form-group-custom">
                      <label className="label-custom">Page Title <span className="req">*</span></label>
                      <input 
                        type="text" 
                        className="input-custom"
                        value={basicInfo.page_title}
                        onChange={(e) => setBasicInfo({ ...basicInfo, page_title: e.target.value })}
                      />
                    </div>
                    <div className="form-group-custom">
                      <label className="label-custom">Short Description <span className="req">*</span></label>
                      <textarea 
                        className="textarea-custom"
                        rows={5}
                        value={basicInfo.short_description}
                        onChange={(e) => setBasicInfo({ ...basicInfo, short_description: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 1: Banner Configuration */}
              {activeTab === 1 && (
                <div className="tab-pane animate-fade-up">
                  <h2 className="edit-faq-panel-title">Banner Configuration</h2>
                  {bannerSlides.map((slide, sIdx) => (
                    <div key={sIdx} className="faq-card-block">
                      <div className="card-header-bar" style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '0.5rem', marginBottom: '1.25rem' }}>
                        <h3 className="card-header-title" style={{ fontSize: '1.1rem', margin: 0 }}>Slide {sIdx + 1}</h3>
                        <button 
                          type="button" 
                          className="trash-btn"
                          style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer' }}
                          onClick={() => handleRemoveSlide(sIdx)}
                        >
                          <FaTrash />
                        </button>
                      </div>

                      <div className="card-content-grid" style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '1.5rem' }}>
                        <div className="image-uploader-box">
                          <label className="label-custom">Slider Image <span className="req">*</span></label>
                          <div 
                            className="image-preview-wrapper"
                            style={{ height: '170px', border: '2px dashed rgba(196,168,130,0.3)', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative' }}
                            onClick={() => fileInputRefs.current[`faq_slide_${sIdx}`]?.click()}
                          >
                            {slide.image ? (
                              <>
                                <img src={getImageUrl(slide.image)} alt={slide.alt} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                <div className="image-delete-overlay" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ef4444', opacity: 0, transition: 'opacity 0.2s' }} onClick={(e) => { e.stopPropagation(); const u = [...bannerSlides]; u[sIdx].image = ''; setBannerSlides(u); }}>
                                  <FaTrash size={20} />
                                </div>
                              </>
                            ) : (
                              <div className="upload-placeholder-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: '#94a3b8' }}>
                                <FaCloudUploadAlt size={32} style={{ color: '#c4a882', marginBottom: '0.5rem' }} />
                                <span style={{ fontSize: '0.85rem' }}>Upload Banner</span>
                              </div>
                            )}
                          </div>
                          <input 
                            type="file" 
                            accept="image/*"
                            style={{ display: 'none' }}
                            ref={el => fileInputRefs.current[`faq_slide_${sIdx}`] = el}
                            onChange={(e) => handleSlideImageChange(e, sIdx)}
                          />
                          <span className="helper-text" style={{ fontSize: '0.78rem' }}>Image dimensions 800 × 500 px.</span>

                          <div className="form-group-custom" style={{ marginTop: '0.75rem' }}>
                            <label className="label-custom">Alt Text <span className="req">*</span></label>
                            <input 
                              type="text" 
                              className="input-custom"
                              placeholder="Alt Text"
                              value={slide.alt || ''}
                              onChange={(e) => {
                                const u = [...bannerSlides];
                                u[sIdx].alt = e.target.value;
                                setBannerSlides(u);
                              }}
                            />
                          </div>
                        </div>

                        <div>
                          <div className="form-group-custom">
                            <label className="label-custom">Slider Title <span className="opt">(Optional)</span></label>
                            <input 
                              type="text" 
                              className="input-custom"
                              placeholder="Slider Title"
                              value={slide.heading || ''}
                              onChange={(e) => {
                                const u = [...bannerSlides];
                                u[sIdx].heading = e.target.value;
                                setBannerSlides(u);
                              }}
                            />
                          </div>
                          <div className="form-group-custom">
                            <label className="label-custom">Slider Description <span className="opt">(Optional)</span></label>
                            <textarea 
                              className="textarea-custom"
                              rows={5}
                              placeholder="Slider Description"
                              value={slide.description || ''}
                              onChange={(e) => {
                                const u = [...bannerSlides];
                                u[sIdx].description = e.target.value;
                                setBannerSlides(u);
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  <div className="add-slide-block" style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
                    <button type="button" className="add-slide-btn" onClick={handleAddSlide} style={{ padding: '0.6rem 1.5rem', background: 'transparent', border: '1px dashed #c4a882', color: '#c4a882', cursor: 'pointer', borderRadius: '6px', fontWeight: '600' }}>
                      <FaPlus /> Add Slide
                    </button>
                  </div>
                </div>
              )}

              {/* TAB 2: FAQs CRUD Table */}
              {activeTab === 2 && (
                <div className="tab-pane animate-fade-up">
                  <div className="edit-faq-panel-title">
                    <span>FAQs</span>
                    <button 
                      type="button" 
                      className="btn-create-faq"
                      onClick={() => handleOpenFaqModal('create')}
                    >
                      <FaPlus /> Create
                    </button>
                  </div>

                  <div className="faq-list-card">
                    <h3 className="faq-list-card-title">FAQ List</h3>
                    <div className="faq-table-responsive">
                    <table className="faq-premium-table">
                      <thead>
                        <tr>
                          <th style={{ width: '40px' }}>#</th>
                          <th style={{ width: '100px' }}>Status</th>
                          <th>FAQ Title</th>
                          <th style={{ width: '96px', textAlign: 'center' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {faqItems.length === 0 ? (
                          <tr>
                            <td colSpan={4} style={{ textAlign: 'center', color: '#94a3b8', fontStyle: 'italic' }}>
                              No FAQs found. Add a new FAQ by clicking &quot;+ Create&quot;.
                            </td>
                          </tr>
                        ) : (
                          faqItems.map((item, idx) => (
                            <tr key={item.id}>
                              <td>{idx + 1}</td>
                              <td>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <label className="switch-toggle" style={{ transform: 'scale(0.8)' }}>
                                    <input 
                                      type="checkbox" 
                                      checked={item.status === 1}
                                      onChange={() => handleToggleFaqItemStatus(item)}
                                    />
                                    <span className="switch-slider"></span>
                                  </label>
                                  <span style={{ fontSize: '0.8rem', color: item.status === 1 ? '#60a5fa' : '#ef4444' }}>
                                    {item.status === 1 ? 'Active' : 'Inactive'}
                                  </span>
                                </div>
                              </td>
                              <td className="faq-title-cell" title={item.faq}>
                                {item.faq}
                              </td>
                              <td style={{ textAlign: 'center', position: 'relative' }}>
                                <div className="faq-actions-dropdown-container">
                                  <button 
                                    type="button" 
                                    className="faq-action-icon edit"
                                    title="Edit FAQ"
                                    onClick={() => setEditFaqTarget(item)}
                                  >
                                    <FaEdit />
                                  </button>
                                  <button 
                                    type="button" 
                                    className="faq-action-icon delete"
                                    title="Delete FAQ"
                                    onClick={() => handleDeleteFaqItem(item)}
                                  >
                                    <FaTrash />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 3: SEO Manager */}
              {activeTab === 3 && (
                <div className="tab-pane animate-fade-up">
                  <h2 className="edit-faq-panel-title">SEO Manager</h2>
                  
                  <div className="faq-card-block">
                    <div className="form-group-custom">
                      <label className="label-custom">Meta Title <span className="req">*</span></label>
                      <input 
                        type="text" 
                        className="input-custom"
                        value={seo.meta_title}
                        onChange={(e) => setSeo({ ...seo, meta_title: e.target.value })}
                      />
                    </div>

                    <div className="form-group-custom">
                      <label className="label-custom">Meta Description <span className="req">*</span></label>
                      <textarea 
                        className="textarea-custom"
                        rows={4}
                        value={seo.meta_description}
                        onChange={(e) => setSeo({ ...seo, meta_description: e.target.value })}
                      />
                    </div>

                    <div className="form-group-custom">
                      <label className="label-custom">Meta Keywords</label>
                      <div className="faq-tags-container">
                        {seo.meta_keywords.map((kw, kIdx) => (
                          <span key={kIdx} className="faq-tag-pill">
                            {kw}
                            <span 
                              className="faq-tag-close-icon"
                              onClick={() => handleRemoveKeyword(kw)}
                            >
                              <FaTimes />
                            </span>
                          </span>
                        ))}
                        <input 
                          type="text" 
                          className="faq-tag-input"
                          placeholder="Type keyword and press Enter or Comma..."
                          value={keywordInput}
                          onChange={(e) => setKeywordInput(e.target.value)}
                          onKeyDown={handleAddKeyword}
                        />
                      </div>
                      <span className="helper-text">Press Enter or comma (,) to add tags.</span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

        </div>
      </div>

      {/* Footer Action Bar */}
      <div className="edit-faq-footer">
        <button 
          type="button" 
          className="btn-cancel"
          onClick={() => navigate('/page-list')}
        >
          Cancel
        </button>
        <button 
          type="button" 
          className="btn-save"
          onClick={handleSaveSettings}
        >
          Save
        </button>
      </div>

      {/* CRUD FAQ Modal Popup */}
      {showFaqModal && (
        <div className="faq-modal-overlay">
          <div className="faq-modal-card">
            <div className="faq-modal-header">
              <h3 className="faq-modal-title">
                {modalMode === 'create' ? 'Create FAQ' : 'Edit FAQ'}
              </h3>
              <button 
                type="button" 
                className="faq-modal-close-btn"
                onClick={() => setShowFaqModal(false)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="faq-modal-body">
              <div className="form-group-custom">
                <label className="label-custom">FAQ Question <span className="req">*</span></label>
                <input 
                  type="text" 
                  className="input-custom"
                  placeholder="Enter FAQ Question..."
                  value={currentFaqItem.faq}
                  onChange={(e) => setCurrentFaqItem({ ...currentFaqItem, faq: e.target.value })}
                />
              </div>

              <div className="form-group-custom">
                <label className="label-custom">FAQ Answer <span className="req">*</span></label>
                <textarea 
                  className="textarea-custom"
                  rows={6}
                  placeholder="Enter FAQ Answer description..."
                  value={currentFaqItem.description}
                  onChange={(e) => setCurrentFaqItem({ ...currentFaqItem, description: e.target.value })}
                />
              </div>

              <div className="form-group-custom" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <label className="label-custom" style={{ marginBottom: 0 }}>Active Status</label>
                <label className="switch-toggle">
                  <input 
                    type="checkbox" 
                    checked={currentFaqItem.status === 1}
                    onChange={(e) => setCurrentFaqItem({ ...currentFaqItem, status: e.target.checked ? 1 : 0 })}
                  />
                  <span className="switch-slider"></span>
                </label>
              </div>
            </div>

            <div className="faq-modal-footer">
              <button 
                type="button" 
                className="btn-cancel" 
                onClick={() => setShowFaqModal(false)}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn-save"
                onClick={handleSaveFaqItem}
              >
                {modalMode === 'create' ? 'Create' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Confirmation Modal */}
      <ConfirmModal
        isOpen={!!editFaqTarget}
        type="edit"
        title="Modify FAQ Item"
        itemName={editFaqTarget?.faq}
        itemType="FAQ item"
        onConfirm={handleConfirmEditFaq}
        onCancel={() => setEditFaqTarget(null)}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!deleteFaqTarget}
        type="delete"
        title="Delete FAQ Item"
        itemName={deleteFaqTarget?.faq}
        itemType="FAQ item"
        loading={isDeletingFaq}
        onConfirm={handleConfirmDeleteFaq}
        onCancel={() => setDeleteFaqTarget(null)}
      />
    </div>
  );
};

export default EditFaq;
